import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  FileText,
  RefreshCw,
  AlertCircle,
  Trash2,
  Download,
  Printer,
  ExternalLink,
  Mail,
  Code2,
  FileDown,
  Database,
  Calendar,
  Layers,
  Receipt,
  Tag,
  BarChart3,
  Wrench,
  Package,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Resolve an appropriate contextual icon for template types
function PreviewTemplateIcon({ template, className }) {
  const type = template?.type || "";
  if (type.startsWith("email_") || template?.channel === "email") {
    return <Mail className={className} />;
  }
  if (type.includes("barcode") || type.includes("label")) {
    return <Tag className={className} />;
  }
  if (type.endsWith("_report")) {
    return <BarChart3 className={className} />;
  }
  if (type.includes("receipt") || type.includes("invoice") || type.includes("quotation")) {
    return <Receipt className={className} />;
  }
  if (type.includes("repair")) {
    return <Wrench className={className} />;
  }
  if (type.includes("purchase") || type.includes("transfer") || type.includes("order")) {
    return <Package className={className} />;
  }
  return <FileText className={className} />;
}

export default function TemplatePreviewModal({
  template,
  open,
  onClose,
  onDelete,
  printerId,
  paperSizeId,
  storeId,
}) {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const currentStoreId = useSelector((state) => state.current_store?.id);
  const hasPermission = (permission) => {
    if (!permissions) return false;
    if (Array.isArray(permission)) return permission.some((p) => permissions.includes(p));
    return permissions.includes(permission);
  };
  const canDeleteTemplates = hasPermission("template delete");
  const canReadTemplates = hasPermission("template read");

  // Fetch template types metadata
  const { data: templateTypes = [] } = useQuery({
    queryKey: ["template-types"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.TEMPLATES.TYPES);
      return res.data?.data ?? res.data ?? [];
    },
    enabled: Boolean(open && canReadTemplates),
    staleTime: 5 * 60 * 1000,
  });

  // Type metadata for the current template
  const currentTypeMeta = useMemo(() => {
    if (!template?.type) return null;
    return templateTypes.find((t) => t.value === template.type) || null;
  }, [templateTypes, template?.type]);

  const isEmail = Boolean(
    currentTypeMeta?.is_email ||
    currentTypeMeta?.channel === "email" ||
    template?.type?.startsWith("email_")
  );

  const isReport = Boolean(
    currentTypeMeta?.is_report ||
    template?.type?.endsWith("_report")
  );

  const supportsRecord = currentTypeMeta?.supports_record ?? !isEmail;

  // View Controls
  const [activeFormat, setActiveFormat] = useState("html"); // "html" | "pdf"
  const [activeMode, setActiveMode] = useState("sample");   // "sample" | "record"
  const sourceId = template?.source_id || "";

  // Default dates: last 30 days
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  // State
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [emailSubject, setEmailSubject] = useState(null);
  const [paperInfo, setPaperInfo] = useState(null);
  const [dataSource, setDataSource] = useState("sample");
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  const iframeRef = useRef(null);
  const pdfBlobUrlRef = useRef(null);

  // Revoke object URLs to avoid memory leaks
  const cleanPdfUrl = useCallback(() => {
    if (pdfBlobUrlRef.current) {
      URL.revokeObjectURL(pdfBlobUrlRef.current);
      pdfBlobUrlRef.current = null;
      setPdfBlobUrl(null);
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanPdfUrl();
    };
  }, [cleanPdfUrl]);

  // Construct request details based on endpoints
  const buildRequestConfig = useCallback(
    (formatOverride = null) => {
      if (!template) return null;

      const format = isEmail ? "html" : (formatOverride || activeFormat);
      const baseParams = {
        format,
        mode: activeMode,
      };

      const resolvedStore = storeId || template.store_id || currentStoreId;
      if (resolvedStore) baseParams.store_id = resolvedStore;
      if (printerId) baseParams.printer_id = printerId;
      if (paperSizeId) baseParams.paper_size_id = paperSizeId;

      if (activeMode === "record") {
        if (sourceId?.trim()) baseParams.source_id = sourceId.trim();
        if (isReport) {
          if (fromDate) baseParams.from = fromDate;
          if (toDate) baseParams.to = toDate;
        }
      }

      // Check if this is an unsaved draft (e.g. from AddTemplate or unsaved edits)
      const isDraft =
        !template.id &&
        (template.content !== undefined || template.styles !== undefined);
      const hasDraftEdits = Boolean(template.hasDraftEdits);

      if (isDraft) {
        return {
          endpoint: API_ENDPOINTS.TEMPLATES.PREVIEW_DRAFT,
          method: "POST",
          data: {
            type: template.type,
            content: template.content || "",
            styles: template.styles || "",
            metadata: template.metadata || {},
            ...baseParams,
          },
          params: {},
          isBlob: format === "pdf",
        };
      }

      if (hasDraftEdits && template.id) {
        return {
          endpoint: API_ENDPOINTS.TEMPLATES.PREVIEW(template.id),
          method: "POST",
          data: {
            type: template.type,
            content: template.content || "",
            styles: template.styles || "",
            metadata: template.metadata || {},
            ...baseParams,
          },
          params: {},
          isBlob: format === "pdf",
        };
      }

      if (template.id) {
        return {
          endpoint: API_ENDPOINTS.TEMPLATES.PREVIEW_SAVED(template.id),
          method: "GET",
          data: null,
          params: baseParams,
          isBlob: format === "pdf",
        };
      }

      if (template.type) {
        return {
          endpoint: API_ENDPOINTS.TEMPLATES.TYPE_PREVIEW(template.type),
          method: "GET",
          data: null,
          params: baseParams,
          isBlob: format === "pdf",
        };
      }

      return null;
    },
    [
      template,
      activeFormat,
      activeMode,
      storeId,
      currentStoreId,
      printerId,
      paperSizeId,
      sourceId,
      isReport,
      isEmail,
      fromDate,
      toDate,
    ]
  );

  const fetchPreview = useCallback(async () => {
    if (!template || !canReadTemplates) return;
    const req = buildRequestConfig();
    if (!req) return;

    setLoading(true);
    setError(null);
    setUsingFallback(false);

    try {
      if (req.isBlob) {
        // PDF format
        const res = await apiClient.request({
          url: req.endpoint,
          method: req.method,
          params: req.method === "GET" ? req.params : undefined,
          data: req.method === "POST" ? req.data : undefined,
          responseType: "blob",
        });

        cleanPdfUrl();
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        pdfBlobUrlRef.current = url;
        setPdfBlobUrl(url);
      } else {
        // HTML format
        const res = await apiClient.request({
          url: req.endpoint,
          method: req.method,
          params: req.method === "GET" ? req.params : undefined,
          data: req.method === "POST" ? req.data : undefined,
        });

        const data = res.data?.data || res.data;
        const html =
          data?.html ||
          (typeof res.data === "string" ? res.data : "");

        if (html) {
          setPreviewHtml(html);
          setEmailSubject(data?.subject || null);
          setPaperInfo(data?.paper || null);
          setDataSource(data?.data_source || activeMode);
          setWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
        } else if (template.content) {
          const fallback = `<style>${template.styles || ""}</style>${template.content}`;
          setPreviewHtml(fallback);
          setUsingFallback(true);
        } else {
          setPreviewHtml(
            "<p style='padding: 24px; font-family: sans-serif; color: #888;'>No preview content available.</p>"
          );
        }
      }
    } catch (err) {
      console.warn("Failed to fetch template preview from server:", err);

      // Graceful fallback to client-side rendered HTML if available
      if (template.content && activeFormat === "html") {
        const fallback = `<style>${template.styles || ""}</style>${template.content}`;
        setPreviewHtml(fallback);
        setUsingFallback(true);
      } else {
        const msg =
          err.response?.data?.message ||
          (typeof err.response?.data === "string" ? err.response.data : null) ||
          err.message ||
          "Failed to generate template preview";
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [template, canReadTemplates, buildRequestConfig, cleanPdfUrl, activeFormat, activeMode]);

  useEffect(() => {
    if (open && template) {
      if (!canReadTemplates) {
        setError("You do not have permission to view templates (missing 'template read' permission).");
        return;
      }
      fetchPreview();
    } else {
      setPreviewHtml("");
      cleanPdfUrl();
      setError(null);
      setUsingFallback(false);
      setWarnings([]);
      setEmailSubject(null);
      setPaperInfo(null);
    }
  }, [open, template, canReadTemplates, fetchPreview, cleanPdfUrl]);

  // Handle PDF Download
  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      let url = pdfBlobUrl;

      if (!url || activeFormat !== "pdf") {
        const req = buildRequestConfig("pdf");
        if (!req) return;
        const res = await apiClient.request({
          url: req.endpoint,
          method: req.method,
          params: req.method === "GET" ? req.params : undefined,
          data: req.method === "POST" ? req.data : undefined,
          responseType: "blob",
        });
        const blob = new Blob([res.data], { type: "application/pdf" });
        url = URL.createObjectURL(blob);
      }

      const fileName = `${template.name || template.type || "template"}-preview.pdf`;
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF downloaded successfully");
    } catch (err) {
      console.error("Failed to download PDF:", err);
      toast.error("Failed to download PDF preview");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Handle Print Preview
  const handlePrint = () => {
    if (activeFormat === "pdf" && pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl, "_blank");
      if (printWindow) {
        printWindow.focus();
      }
    } else if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };

  // Open Preview in New Window
  const handleOpenNewTab = () => {
    if (activeFormat === "pdf" && pdfBlobUrl) {
      window.open(pdfBlobUrl, "_blank");
    } else if (previewHtml) {
      const blob = new Blob([previewHtml], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  };

  if (!template) return null;

  const displayTypeLabel =
    currentTypeMeta?.label ||
    template.type_label ||
    template.type?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
    "Document";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[92vh] flex flex-col p-0 gap-0 overflow-hidden bg-background">
        {/* Header */}
        <DialogHeader className="px-6 py-3.5 border-b border-border bg-card shrink-0 relative pr-28">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
              <PreviewTemplateIcon template={template} className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold flex items-center gap-2">
                <span className="truncate">{template.name || displayTypeLabel}</span>
                {template.is_system && (
                  <Badge
                    variant="outline"
                    className="text-[10px] font-normal text-muted-foreground px-1.5 py-0 h-4 bg-muted/40 shrink-0"
                  >
                    System Default
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                <span>
                  Type: <strong className="text-foreground font-medium">{displayTypeLabel}</strong>
                </span>
                {!isEmail && paperInfo && (
                  <>
                    <span>•</span>
                    <span>
                      Paper: {paperInfo.size?.toUpperCase()}{" "}
                      {paperInfo.orientation ? `(${paperInfo.orientation})` : ""}
                    </span>
                  </>
                )}
              </DialogDescription>
            </div>
          </div>

          {/* Top-Right Action Controls (aligned with the DialogClose X button) */}
          <div className="absolute right-12 top-3 flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenNewTab}
              disabled={loading}
              className="h-8 w-8 text-muted-foreground hover:text-foreground cursor-pointer"
              title="Open preview in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchPreview}
              disabled={loading}
              className="h-8 w-8 text-muted-foreground hover:text-foreground cursor-pointer"
              title="Refresh preview"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </DialogHeader>

        {/* Toolbar & Filter Sub-bar */}
        <div className="px-6 py-2.5 border-b border-border bg-muted/30 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            {/* Format toggle: HTML vs PDF (Only applicable for printable documents, not emails) */}
            {!isEmail && (
              <div className="inline-flex rounded-lg border border-border/60 bg-muted/50 p-0.5 shadow-2xs">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveFormat("html")}
                  className={cn(
                    "h-7 px-3 text-xs gap-1.5 rounded-md font-medium cursor-pointer transition-all",
                    activeFormat === "html"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Code2 className="h-3.5 w-3.5" />
                  HTML View
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveFormat("pdf")}
                  className={cn(
                    "h-7 px-3 text-xs gap-1.5 rounded-md font-medium cursor-pointer transition-all",
                    activeFormat === "pdf"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <FileDown className="h-3.5 w-3.5" />
                  DomPDF View
                </Button>
              </div>
            )}

            {/* Mode toggle: Sample Data vs Real Tenant Record */}
            {supportsRecord && (
              <div className="inline-flex rounded-lg border border-border/60 bg-muted/50 p-0.5 shadow-2xs">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveMode("sample")}
                  className={cn(
                    "h-7 px-3 text-xs gap-1.5 rounded-md font-medium cursor-pointer transition-all",
                    activeMode === "sample"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  title="Generate realistic mock data"
                >
                  <Layers className="h-3.5 w-3.5" />
                  Sample Data
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveMode("record")}
                  className={cn(
                    "h-7 px-3 text-xs gap-1.5 rounded-md font-medium cursor-pointer transition-all",
                    activeMode === "record"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  title="Preview with real tenant record"
                >
                  <Database className="h-3.5 w-3.5" />
                  Live Record
                </Button>
              </div>
            )}
          </div>

          {/* Report Date Range Filter (only for reports when in live record mode) */}
          {activeMode === "record" && supportsRecord && isReport && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <Label className="text-[11px] text-muted-foreground">From:</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="h-7 text-xs w-32 px-2"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <Label className="text-[11px] text-muted-foreground">To:</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="h-7 text-xs w-32 px-2"
                />
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={fetchPreview}
                className="h-7 text-xs px-2.5 cursor-pointer"
              >
                Apply Range
              </Button>
            </div>
          )}
        </div>

        {/* Email Subject Banner */}
        {isEmail && emailSubject && (
          <div className="bg-muted/40 border-b border-border/60 px-6 py-2 flex items-center gap-2 text-xs shrink-0">
            <Mail className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="font-semibold text-muted-foreground">Subject:</span>
            <span className="font-medium text-foreground">{emailSubject}</span>
          </div>
        )}

        {/* Server Warnings */}
        {warnings.length > 0 && (
          <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-2 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 shrink-0">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              {warnings.map((w, idx) => (
                <p key={idx}>{w}</p>
              ))}
            </div>
          </div>
        )}

        {/* Client Fallback Notice */}
        {usingFallback && (
          <div className="bg-blue-500/10 border-b border-blue-500/20 px-6 py-1.5 flex items-center gap-2 text-xs text-blue-700 dark:text-blue-400 shrink-0">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            <span>
              Server preview unavailable; displaying local draft layout.
            </span>
          </div>
        )}

        {/* Padded Document Viewport (without outer container scrollbars) */}
        <div className="flex-1 min-h-0 w-full p-4 sm:p-5 bg-muted/20 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
              <Spinner className="h-7 w-7 text-primary" />
              <span className="text-xs">
                {activeFormat === "pdf"
                  ? "Rendering DomPDF document…"
                  : `Generating ${activeMode === "record" ? "live record" : "sample"} preview…`}
              </span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center max-w-sm mx-auto px-4">
              <div className="h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
                <AlertCircle className="h-6 w-6" />
              </div>
              <h5 className="text-sm font-semibold text-foreground">
                Unable to load preview
              </h5>
              <p className="text-xs text-muted-foreground">{error}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchPreview}
                className="mt-2 text-xs gap-1.5 cursor-pointer"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Try Again
              </Button>
            </div>
          ) : (
            <div className="w-full h-full bg-white dark:bg-card rounded-lg border border-border/70 shadow-2xs overflow-hidden flex flex-col">
              {activeFormat === "pdf" && pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  title={template.name || "PDF Preview"}
                  className="w-full h-full border-0 block flex-1 bg-white"
                />
              ) : (
                <iframe
                  ref={iframeRef}
                  srcDoc={previewHtml}
                  title={template.name || "Template Preview"}
                  className="w-full h-full border-0 block flex-1 bg-white"
                  sandbox="allow-same-origin allow-scripts allow-popups"
                />
              )}
            </div>
          )}
        </div>

        {/* Standard Modal Footer */}
        <DialogFooter className="px-6 py-3 border-t border-border bg-card flex flex-row items-center justify-between sm:justify-between shrink-0">
          <div className="flex items-center gap-2">
            {!template.is_system && onDelete && canDeleteTemplates && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onClose();
                  onDelete(template);
                }}
                className="h-8 px-2.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 cursor-pointer"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete Template
              </Button>
            )}
            {dataSource && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Source: <strong className="capitalize font-medium text-foreground">{dataSource}</strong>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!isEmail && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrint}
                  disabled={loading}
                  className="h-8 px-3 text-xs gap-1.5 cursor-pointer"
                >
                  <Printer className="h-3.5 w-3.5" />
                  <span>Print</span>
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={loading || downloadingPdf}
                  className="h-8 px-3 text-xs gap-1.5 cursor-pointer"
                >
                  {downloadingPdf ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  <span>Download PDF</span>
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
